import { Link } from "react-router-dom";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import { getBaseUrl, buildBreadcrumbSchema } from "@/lib/seo";
import {
  TERMS_DOCUMENT_ID,
  TERMS_EFFECTIVE_DATE_LABEL,
  TERMS_VERSION,
  TERMS_WITHDRAWAL_DAYS,
} from "@/lib/terms-metadata";
import { STRIPE_PLANS } from "@/lib/stripe-plans";

const TERMS_TITLE = "Termos de Serviço | FabChat";
const TERMS_DESCRIPTION =
  "Termos de Serviço da FabChat. Condições de uso da plataforma de agentes de IA para WhatsApp e Instagram.";

const breadcrumbItems = [
  { name: "Início", path: "/" },
  { name: "Termos de Serviço", path: "/termos" },
];

export default function TermsOfService() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, breadcrumbItems);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={TERMS_TITLE}
        description={TERMS_DESCRIPTION}
        canonicalPath="/termos"
      />
      <StructuredData data={breadcrumbSchema} />
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center font-bold text-xl">
            <img src="/logo-light.png?v=6" alt="FabChat Logo" className="h-10 w-auto object-contain block dark:hidden" />
            <img src="/logo.png?v=6" alt="FabChat Logo" className="h-10 w-auto object-contain hidden dark:block" />
          </Link>
          <Link 
            to="/" 
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Voltar ao início
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-4">Termos de Serviço</h1>
          <p className="text-muted-foreground mb-4">
            <strong>Última atualização:</strong> {TERMS_EFFECTIVE_DATE_LABEL}
          </p>
          <p className="text-muted-foreground mb-8 text-sm">
            <strong>Identificação do documento:</strong> {TERMS_DOCUMENT_ID} (versão {TERMS_VERSION})
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Aceitação dos Termos e Formação do Contrato</h2>
            <p className="mb-4">
              Ao acessar e utilizar os serviços da FabChat, você concorda em cumprir e estar vinculado a estes Termos de Serviço. Se você não concordar com qualquer parte destes termos, não deve utilizar nossos serviços.
            </p>
            <p className="mb-4">
              Estes Termos, juntamente com a Política de Privacidade, a Política de Cookies e as condições específicas do plano escolhido no momento da contratação (incluindo periodicidade, preço, compromisso mínimo e forma de cobrança), constituem um <strong>contrato eletrônico</strong> entre você ("Usuário", "Cliente" ou "Contratante") e a FabChat ("nós", "nosso", "Contratada" ou "empresa").
            </p>
            <p className="mb-4">
              O aceite é formalizado quando você marca a opção de concordância, conclui o cadastro, efetua o pagamento ou continua utilizando a plataforma após ciência destes Termos. Esse aceite eletrônico produz os mesmos efeitos jurídicos de um contrato escrito e assinado, nos termos da legislação brasileira aplicável, inclusive do Marco Civil da Internet (Lei nº 12.965/2014) e do Código de Defesa do Consumidor (Lei nº 8.078/1990 — "CDC"), quando aplicável.
            </p>
            <p className="mb-4">
              A FabChat poderá registrar, para fins de prova e conformidade, a data e hora do aceite, o identificador da conta, o endereço de e-mail cadastrado, a versão destes Termos ({TERMS_VERSION}) e demais dados técnicos necessários à comprovação da manifestação de vontade.
            </p>
            <p className="mb-4">
              A contratação de planos pagos somente se completa após a confirmação do pagamento pelo provedor de pagamento e a ativação da assinatura na plataforma. Até então, eventuais valores exibidos no site ou no checkout têm caráter informativo e poderão ser atualizados conforme a Seção 9.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Descrição dos Serviços</h2>
            <p className="mb-4">
              A FabChat é uma plataforma de software como serviço (SaaS) que oferece:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Automação de atendimento via WhatsApp e Instagram utilizando inteligência artificial</li>
              <li>Gestão de conversas e leads através de interface unificada</li>
              <li>Configuração e personalização de agentes de IA para diferentes funções (vendas, suporte, SDR, agendamentos)</li>
              <li>Integração com plataformas de terceiros (WhatsApp Business API, Instagram, Google Calendar, etc.)</li>
              <li>Análise e relatórios de desempenho dos agentes</li>
              <li>Outros recursos e funcionalidades disponibilizados através da plataforma</li>
            </ul>
            <p className="mb-4">
              Reservamo-nos o direito de modificar, suspender ou descontinuar qualquer aspecto dos serviços a qualquer momento, com ou sem aviso prévio.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.1. Serviços Profissionais Opcionais</h3>
            <p className="mb-4">
              A assinatura da Plataforma FabChat compreende, salvo disposição expressa em contrário, o acesso às funcionalidades, recursos e limites correspondentes ao plano contratado.
            </p>
            <p className="mb-4">
              Serviços de implementação, implantação, onboarding assistido, configuração, personalização, criação ou otimização de agentes de inteligência artificial, elaboração ou revisão de prompts, configuração de regras de negócio, integrações, migração ou organização de dados, treinamento, acompanhamento estratégico, consultoria e outros serviços executados por profissionais da FabChat (“Serviços Profissionais”) não estão incluídos automaticamente na assinatura da Plataforma.
            </p>
            <p className="mb-4">
              Os Serviços Profissionais são opcionais e somente serão prestados quando contratados expressamente pelo Cliente, mediante proposta comercial, ordem de serviço, checkout, aceite eletrônico ou outro instrumento que identifique de forma suficiente o serviço contratado e suas respectivas condições.
            </p>
            <p className="mb-4">
              A contratação de um plano da FabChat não obriga o Cliente a contratar Serviços Profissionais, assim como a contratação de Serviços Profissionais não altera automaticamente o plano, os limites ou as funcionalidades da assinatura da Plataforma.
            </p>
            <p className="mb-4">
              Determinados Serviços Profissionais poderão depender da existência e manutenção de assinatura ativa em plano compatível com os recursos necessários à sua execução.
            </p>
            <p className="mb-4">
              A contratação de Serviços Profissionais não concede acesso a funcionalidades, integrações, canais, limites ou recursos não contemplados no plano contratado. Caso determinado serviço dependa de recurso disponível apenas em plano superior, sua execução poderá ficar condicionada ao respectivo upgrade.
            </p>
            <p className="mb-4">
              Upgrade, downgrade ou qualquer outra alteração posterior do plano contratado não gera direito automático a nova implementação, reconfiguração, migração, integração, parametrização ou adaptação do ambiente. Tais atividades poderão constituir novo Serviço Profissional, sujeito a contratação específica.
            </p>
            <p className="mb-4">
              Quando contratados, o escopo, entregáveis, quantidade de horas ou reuniões, cronograma, prazo estimado, responsabilidades das partes, número de revisões, preço, forma de pagamento e demais condições específicas poderão ser definidos na respectiva proposta comercial ou instrumento de contratação.
            </p>
            <p className="mb-4">
              Em caso de divergência entre estes Termos e uma proposta comercial, ordem de serviço ou instrumento específico aceito pelo Cliente, as condições específicas desse instrumento prevalecerão exclusivamente em relação aos Serviços Profissionais nele contratados, permanecendo estes Termos aplicáveis às demais relações entre as partes.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.2. Início e Execução dos Serviços Profissionais</h3>
            <p className="mb-4">
              Salvo disposição diversa no instrumento de contratação, a execução dos Serviços Profissionais poderá ficar condicionada, conforme aplicável, à:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>formalização da contratação;</li>
              <li>confirmação do pagamento inicial ou da condição financeira acordada;</li>
              <li>existência de plano ativo e compatível com o serviço;</li>
              <li>disponibilização das informações, documentos e materiais necessários;</li>
              <li>concessão dos acessos, permissões e credenciais necessários à execução; e</li>
              <li>cumprimento de outras dependências expressamente informadas ao Cliente.</li>
            </ul>
            <p className="mb-4">
              Prazos de execução somente começarão a ser contados após o atendimento das condições necessárias ao efetivo início do serviço, salvo disposição expressa em contrário.
            </p>
            <p className="mb-4">
              Cronogramas e prazos informados poderão ser estimativos e variar de acordo com a complexidade do projeto, volume de informações, quantidade de integrações, dependências técnicas, necessidade de validações e tempo de resposta do Cliente.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.3. Colaboração e Responsabilidades do Cliente</h3>
            <p className="mb-4">
              A prestação dos Serviços Profissionais poderá depender do fornecimento, pelo Cliente, de informações, documentos, materiais, acessos, credenciais, regras de negócio, catálogos, políticas comerciais, fluxos de atendimento, exemplos de conversas e demais elementos necessários à execução do serviço.
            </p>
            <p className="mb-4">
              O Cliente é responsável pela exatidão, integridade, licitude e atualização das informações fornecidas à FabChat, bem como por garantir que possui as autorizações necessárias para disponibilizar materiais, dados, contas, sistemas, acessos e credenciais utilizados na execução do serviço.
            </p>
            <p className="mb-4">
              A ausência, atraso ou fornecimento incompleto de informações, acessos, aprovações ou outros elementos necessários poderá afetar ou suspender o cronograma e os resultados da implementação, sem caracterizar descumprimento pela FabChat.
            </p>
            <p className="mb-4">
              Nessas situações, os prazos poderão ser reprogramados de acordo com a disponibilidade operacional e de agenda da FabChat após a regularização das pendências pelo Cliente.
            </p>
            <p className="mb-4">
              Caso o projeto dependa de reuniões, validações ou interações agendadas, ausências, cancelamentos ou reagendamentos solicitados pelo Cliente poderão afetar o cronograma.
            </p>
            <p className="mb-4">
              Caberá ao Cliente revisar, testar e aprovar as configurações, agentes, prompts, automações, integrações e demais entregáveis antes de sua utilização definitiva em ambiente de produção.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.4. Escopo, Alterações e Viabilidade Técnica</h3>
            <p className="mb-4">
              Os Serviços Profissionais serão executados dentro do escopo contratado e das capacidades técnicas disponíveis na Plataforma, no plano contratado e nas integrações suportadas no momento da execução.
            </p>
            <p className="mb-4">
              A contratação de um Serviço Profissional não implica obrigação de desenvolver novas funcionalidades, modificar sistemas externos, criar integrações não suportadas ou executar atividades que não estejam expressamente compreendidas no escopo contratado.
            </p>
            <p className="mb-4">
              Solicitações que representem novas funcionalidades, alterações relevantes de regras de negócio, novas integrações, novos agentes, mudanças estruturais, novas automações, reconfigurações ou quaisquer atividades que excedam o escopo originalmente contratado poderão ser tratadas como serviço adicional.
            </p>
            <p className="mb-4">
              Nesses casos, a FabChat poderá apresentar nova estimativa de prazo e orçamento, cuja execução ficará condicionada ao aceite do Cliente.
            </p>
            <p className="mb-4">
              Correções necessárias para adequar uma entrega ao escopo originalmente contratado não serão consideradas ampliação de escopo. Alterações decorrentes de nova solicitação, mudança de estratégia, preferência, processo, regra de negócio ou requisito apresentado posteriormente pelo Cliente poderão ser consideradas novo escopo.
            </p>
            <p className="mb-4">
              Alterações relevantes de escopo deverão ser formalizadas por escrito, inclusive por proposta complementar, ordem de serviço, e-mail, mensagem eletrônica ou outro meio que permita comprovar o respectivo aceite.
            </p>
            <p className="mb-4">
              Caso, durante a execução, seja identificada incompatibilidade técnica, limitação da Plataforma, restrição de terceiro ou circunstância que impeça a execução de requisito inicialmente previsto, a FabChat informará o Cliente para que as partes possam avaliar alternativa tecnicamente viável ou eventual ajuste do escopo.
            </p>
            <p className="mb-4">
              A existência de requisito pretendido pelo Cliente não implica obrigação de desenvolvimento ou modificação da Plataforma quando tal funcionalidade não fizer parte das capacidades disponíveis ou do escopo expressamente contratado.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.5. Conclusão e Aceite dos Serviços Profissionais</h3>
            <p className="mb-4">
              Salvo critérios diferentes definidos no respectivo instrumento de contratação, um Serviço Profissional será considerado concluído quando os entregáveis previstos no escopo tiverem sido realizados e disponibilizados ao Cliente em condições de utilização ou avaliação.
            </p>
            <p className="mb-4">
              A conclusão do Serviço Profissional não depende da obtenção de resultado comercial, do início efetivo da utilização pelo Cliente ou da decisão do Cliente de colocar imediatamente a solução em produção.
            </p>
            <p className="mb-4">
              A FabChat poderá comunicar ao Cliente a conclusão do serviço pelos canais utilizados durante o projeto.
            </p>
            <p className="mb-4">
              Salvo prazo diverso previsto na proposta comercial, o Cliente terá 10 (dez) dias úteis contados da comunicação de conclusão para informar, de forma suficientemente detalhada, eventuais inconsistências relacionadas ao escopo contratado.
            </p>
            <p className="mb-4">
              Inconsistências comprovadamente relacionadas ao escopo contratado serão analisadas e, quando cabível, ajustadas pela FabChat.
            </p>
            <p className="mb-4">
              Solicitações de melhoria, mudança de preferência, ampliação de escopo, novas funcionalidades ou alterações não previstas originalmente não serão consideradas inconsistências da implementação e poderão depender de nova contratação.
            </p>
            <p className="mb-4">
              Na ausência de manifestação do Cliente dentro do prazo aplicável, os entregáveis serão considerados aceitos, sem prejuízo de eventual correção de vícios ou obrigações que não possam ser afastadas pela legislação aplicável.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.6. Suporte e Atividades Após a Conclusão</h3>
            <p className="mb-4">
              O suporte prestado durante a execução dos Serviços Profissionais limita-se às atividades necessárias ao cumprimento do escopo contratado.
            </p>
            <p className="mb-4">
              Após a conclusão do projeto, o suporte relacionado ao uso da Plataforma observará as condições, canais e limites do plano contratado, salvo quando houver contratação específica de acompanhamento, manutenção, consultoria ou suporte adicional.
            </p>
            <p className="mb-4">
              A conclusão de uma implementação, configuração ou consultoria não cria obrigação permanente de a FabChat atualizar, revisar, otimizar ou manter continuamente prompts, agentes, automações, bases de conhecimento, integrações, fluxos, regras de negócio ou demais configurações implementadas.
            </p>
            <p className="mb-4">
              Atualizações, reconfigurações estruturais, novas integrações, alterações de processos ou outros trabalhos solicitados após a conclusão poderão constituir novos Serviços Profissionais.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.7. Natureza e Limites dos Serviços Profissionais</h3>
            <p className="mb-4">
              Os Serviços Profissionais constituem obrigação de meio, e não garantia de resultado comercial específico.
            </p>
            <p className="mb-4">
              A FabChat empregará esforços tecnicamente razoáveis para executar os serviços conforme o escopo contratado, mas não garante resultados específicos de vendas, conversão, faturamento, geração de leads, atendimento, produtividade ou qualquer outro indicador de negócio.
            </p>
            <p className="mb-4">
              A participação da FabChat na implementação, configuração ou consultoria não constitui garantia de que respostas geradas por inteligência artificial serão sempre precisas, completas, adequadas ou livres de comportamentos inesperados, observadas as limitações de responsabilidade previstas nestes Termos e na legislação aplicável.
            </p>
            <p className="mb-4">
              A contratação de implementação ou consultoria não elimina as limitações inerentes às tecnologias de inteligência artificial nem a necessidade de supervisão, validação e acompanhamento pelo Cliente.
            </p>
            <p className="mb-4">
              Os Serviços Profissionais permanecem sujeitos às demais disposições sobre disponibilidade, inteligência artificial, integrações, limitações de responsabilidade e força maior previstas nestes Termos.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.8. Confidencialidade nos Serviços Profissionais</h3>
            <p className="mb-4">
              Durante a execução dos Serviços Profissionais, as partes poderão ter acesso a informações comerciais, estratégicas, técnicas, operacionais ou outras informações não públicas da outra parte.
            </p>
            <p className="mb-4">
              Cada parte deverá preservar a confidencialidade das informações dessa natureza recebidas em razão do projeto e utilizá-las somente para as finalidades relacionadas à contratação, salvo quando houver autorização para finalidade diversa ou obrigação legal de divulgação.
            </p>
            <p className="mb-4">
              Não serão consideradas confidenciais informações que sejam publicamente disponíveis sem violação destes Termos, que já fossem legitimamente conhecidas pela parte receptora, que tenham sido obtidas licitamente de terceiro sem obrigação de sigilo ou cuja divulgação seja exigida por lei, ordem judicial ou autoridade competente.
            </p>
            <p className="mb-4">
              As regras específicas de proteção e tratamento de dados pessoais permanecem sujeitas à Política de Privacidade e ao Contrato de Processamento de Dados previsto nestes Termos.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.9. Equipe e Prestadores Envolvidos</h3>
            <p className="mb-4">
              A FabChat poderá executar os Serviços Profissionais por meio de seus sócios, empregados, colaboradores, prestadores de serviços ou parceiros contratados, permanecendo responsável pelo cumprimento das obrigações que lhe sejam aplicáveis.
            </p>
            <p className="mb-4">
              Sempre que tais pessoas tiverem acesso a informações confidenciais ou dados pessoais, deverão observar as obrigações de confidencialidade, segurança e proteção de dados aplicáveis à respectiva atividade.
            </p>
            <p className="mb-4">
              A prestação dos Serviços Profissionais não cria vínculo trabalhista, sociedade, associação, representação comercial, mandato ou relação de exclusividade entre o Cliente e a FabChat ou entre o Cliente e os profissionais envolvidos na execução do projeto.
            </p>

            <h3 className="text-xl font-semibold mb-3">2.10. Vigência dos Serviços Profissionais</h3>
            <p className="mb-4">
              Cada Serviço Profissional permanecerá vigente pelo período necessário à execução de seu respectivo escopo, salvo conclusão, cancelamento, rescisão ou outra hipótese prevista nestes Termos ou no instrumento específico de contratação.
            </p>
            <p className="mb-4">
              A conclusão ou encerramento do Serviço Profissional não extingue obrigações que, por sua natureza, devam permanecer eficazes posteriormente, incluindo obrigações de pagamento já constituídas, confidencialidade, propriedade intelectual, proteção de dados e responsabilidades decorrentes de fatos ocorridos durante a execução.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. Cadastro e Conta de Usuário</h2>
            
            <h3 className="text-xl font-semibold mb-3">3.1. Elegibilidade</h3>
            <p className="mb-4">
              Para utilizar nossos serviços, você deve:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Ter pelo menos 18 anos de idade ou ser uma pessoa jurídica devidamente constituída</li>
              <li>Fornecer informações precisas, completas e atualizadas durante o cadastro</li>
              <li>Manter a segurança de sua conta e senha</li>
              <li>Ser responsável por todas as atividades que ocorrem em sua conta</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">3.2. Responsabilidades do Usuário</h3>
            <p className="mb-4">Você é responsável por:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Manter a confidencialidade de suas credenciais de acesso</li>
              <li>Notificar-nos imediatamente sobre qualquer uso não autorizado de sua conta</li>
              <li>Garantir que todas as informações fornecidas sejam precisas e atualizadas</li>
              <li>Cumprir todas as leis e regulamentos aplicáveis ao usar nossos serviços</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Uso Aceitável</h2>
            <p className="mb-4">Você concorda em NÃO utilizar nossos serviços para:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Qualquer atividade ilegal, fraudulenta ou não autorizada</li>
              <li>Enviar spam, mensagens não solicitadas ou conteúdo abusivo</li>
              <li>Violar direitos de propriedade intelectual de terceiros</li>
              <li>Interferir ou interromper a operação dos serviços ou servidores</li>
              <li>Tentar obter acesso não autorizado a sistemas, contas ou dados</li>
              <li>Utilizar os serviços de forma que possa danificar, desabilitar ou sobrecarregar nossa infraestrutura</li>
              <li>Coletar ou armazenar dados pessoais de terceiros sem o consentimento adequado ou base legal</li>
              <li>Violar políticas de privacidade ou termos de serviço de plataformas integradas (WhatsApp, Instagram, etc.)</li>
              <li>Utilizar os serviços para atividades que violem a LGPD ou outras leis de proteção de dados</li>
              <li>Violar políticas de uso aceitável de plataformas integradas (WhatsApp, Instagram, Google, entre outras)</li>
              <li>Enviar mensagens em massa não solicitadas ou spam através de plataformas integradas</li>
              <li>Utilizar APIs de plataformas terceiras de forma não autorizada ou em violação aos seus termos</li>
              <li>Compartilhar, vender ou transferir credenciais de acesso a plataformas terceiras</li>
              <li>Exceder limites de taxa ou uso estabelecidos por plataformas terceiras</li>
            </ul>
            <p className="mb-4">
              Reservamo-nos o direito de suspender ou encerrar sua conta imediatamente se você violar estes termos ou utilizar os serviços de forma inadequada.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Banimento, Suspensão e Penalidades</h2>
            
            <h3 className="text-xl font-semibold mb-3">5.1. Condutas Proibidas e Uso Indevido</h3>
            <p className="mb-4">
              <strong>CONDUTAS GRAVEMENTE PROIBIDAS:</strong> As seguintes condutas são estritamente proibidas e resultarão em medidas disciplinares imediatas, incluindo suspensão, banimento permanente e/ou ações legais:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Exploração de glitches, bugs ou vulnerabilidades:</strong> Utilizar falhas técnicas, erros de sistema ou vulnerabilidades de segurança para obter vantagens indevidas, acessar recursos não autorizados ou causar prejuízos à plataforma</li>
              <li><strong>Uso não autorizado de recursos premium:</strong> Acessar, utilizar ou tentar acessar funcionalidades, recursos ou serviços premium sem possuir assinatura válida e ativa</li>
              <li><strong>Manipulação de assinaturas, limites ou cobranças:</strong> Tentar alterar, burlar ou manipular sistemas de verificação de assinatura, limites de uso, valores de cobrança ou status de pagamento</li>
              <li><strong>Bypass de sistemas de segurança ou autenticação:</strong> Tentar contornar, desabilitar ou burlar sistemas de segurança, autenticação, autorização ou controle de acesso</li>
              <li><strong>Uso de ferramentas automatizadas para burlar limites:</strong> Utilizar scripts, bots, ferramentas automatizadas ou qualquer meio técnico para exceder limites de uso, burlar restrições ou obter acesso não autorizado</li>
              <li><strong>Criação de múltiplas contas para evitar limites:</strong> Criar, manter ou utilizar múltiplas contas, perfis ou identidades para evitar limites de uso, obter múltiplos períodos de teste ou acessar recursos premium gratuitamente</li>
              <li><strong>Compartilhamento não autorizado de credenciais premium:</strong> Compartilhar, vender, transferir ou permitir que terceiros utilizem credenciais de acesso, tokens de autenticação ou acesso a recursos premium</li>
              <li><strong>Manipulação de dados de uso ou métricas:</strong> Tentar alterar, falsificar ou manipular dados de uso, métricas, logs ou relatórios do sistema</li>
              <li><strong>Interferência em sistemas de cobrança:</strong> Tentar evitar, reduzir ou manipular valores de cobrança, taxas, overages ou custos de uso</li>
              <li><strong>Uso de métodos fraudulentos de pagamento:</strong> Utilizar cartões de crédito roubados, clonados, falsificados ou métodos de pagamento não autorizados</li>
              <li><strong>Reversão fraudulenta de pagamentos:</strong> Solicitar chargebacks ou estornos fraudulentos após utilizar os serviços</li>
              <li><strong>Distribuição de métodos de exploração:</strong> Compartilhar, ensinar, vender ou distribuir métodos, ferramentas ou instruções para explorar vulnerabilidades ou burlar sistemas</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">5.2. Exploração de Glitches, Bugs e Vulnerabilidades</h3>
            <p className="mb-4">
              <strong>DEFINIÇÃO DE EXPLORAÇÃO:</strong> Considera-se exploração qualquer ação intencional de:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Utilizar falhas técnicas, erros de programação ou vulnerabilidades de segurança para obter benefícios indevidos</li>
              <li>Repetir ações que exploram bugs conhecidos ou desconhecidos</li>
              <li>Não reportar bugs encontrados quando estes proporcionam vantagens indevidas</li>
              <li>Compartilhar ou distribuir informações sobre bugs que permitam exploração</li>
            </ul>
            <p className="mb-4">
              <strong>OBRIGAÇÃO DE REPORTAR:</strong> Se você descobrir qualquer bug, glitch ou vulnerabilidade, você é <strong>obrigado</strong> a:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Reportar imediatamente à FabChat através de canais oficiais (atendimento.fabtech@gmail.com)</li>
              <li>Não explorar, utilizar ou compartilhar informações sobre a vulnerabilidade</li>
              <li>Fornecer detalhes suficientes para que possamos corrigir o problema</li>
              <li>Aguardar a correção antes de utilizar novamente a funcionalidade afetada</li>
            </ul>
            <p className="mb-4">
              <strong>EXEMPLOS ESPECÍFICOS DE EXPLORAÇÃO PROIBIDA:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Bypass de verificação de assinatura para acessar recursos premium gratuitamente</li>
              <li>Manipulação de limites de uso (LLM outputs, mensagens, leads, agentes) através de bugs</li>
              <li>Reset ou manipulação de períodos de cobrança ou contadores de uso</li>
              <li>Acesso a funcionalidades de planos superiores sem pagamento</li>
              <li>Duplicação de recursos, créditos ou benefícios através de falhas técnicas</li>
              <li>Bypass de sistemas de rate limiting ou throttling</li>
              <li>Manipulação de dados de assinatura ou status de pagamento</li>
            </ul>
            <p className="mb-4">
              <strong>RESPONSABILIDADE POR DANOS:</strong> Qualquer exploração de bugs ou vulnerabilidades resultará em:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Responsabilização por todos os custos e danos causados à plataforma</li>
              <li>Cobrança retroativa de valores que deveriam ter sido pagos</li>
              <li>Compensação por custos de investigação, correção e prevenção</li>
              <li>Medidas disciplinares imediatas, incluindo banimento permanente</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">5.3. Uso Não Autorizado de Recursos Premium</h3>
            <p className="mb-4">
              <strong>DEFINIÇÃO DE RECURSOS PREMIUM:</strong> Recursos premium incluem, mas não se limitam a:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Funcionalidades exclusivas de planos pagos (agentes adicionais, limites aumentados, integrações avançadas)</li>
              <li>Acesso a APIs ou endpoints premium</li>
              <li>Recursos de análise, relatórios ou dashboards avançados</li>
              <li>Suporte prioritário ou dedicado</li>
              <li>Qualquer funcionalidade que exija assinatura ativa e válida</li>
            </ul>
            <p className="mb-4">
              <strong>PROIBIÇÕES ESPECÍFICAS:</strong> É estritamente proibido:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Acessar recursos premium sem assinatura válida:</strong> Tentar acessar, utilizar ou ativar qualquer recurso premium sem possuir assinatura ativa e válida do plano correspondente</li>
              <li><strong>Manipular status de assinatura:</strong> Tentar alterar, falsificar ou manipular o status, data de expiração ou tipo de assinatura através de qualquer meio técnico ou não autorizado</li>
              <li><strong>Burlar verificação de assinatura:</strong> Utilizar ferramentas, scripts, modificações de código, interceptação de requisições ou qualquer método para contornar sistemas de verificação de assinatura</li>
              <li><strong>Compartilhar acesso premium:</strong> Permitir que terceiros utilizem sua conta ou credenciais para acessar recursos premium, ou compartilhar tokens, chaves de API ou outros meios de autenticação</li>
              <li><strong>Utilizar contas de terceiros:</strong> Acessar recursos premium utilizando contas, credenciais ou assinaturas de outras pessoas sem autorização</li>
              <li><strong>Manipular limites de uso:</strong> Tentar aumentar, resetar ou burlar limites de uso (mensagens, LLM outputs, agentes, leads) através de qualquer meio não autorizado</li>
            </ul>
            <p className="mb-4">
              <strong>RESPONSABILIDADE POR USO NÃO AUTORIZADO:</strong> Qualquer uso não autorizado de recursos premium resultará em:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Cobrança imediata e retroativa de todos os valores que deveriam ter sido pagos</li>
              <li>Cálculo de uso não autorizado baseado em tarifas do plano correspondente</li>
              <li>Multa adicional de até 20% (vinte por cento) sobre o valor devido</li>
              <li>Banimento permanente imediato da plataforma</li>
              <li>Ações legais para recuperação de valores e indenização por danos</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">5.4. Penalidades e Medidas Disciplinares</h3>
            <p className="mb-4">
              <strong>SUSPENSÃO TEMPORÁRIA:</strong> A FabChat pode suspender temporariamente sua conta e acesso aos serviços por:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Primeira violação leve:</strong> Suspensão de 7 (sete) a 30 (trinta) dias</li>
              <li><strong>Segunda violação ou violação moderada:</strong> Suspensão de 30 (trinta) a 90 (noventa) dias</li>
              <li><strong>Violação grave ou repetida:</strong> Suspensão de 90 (noventa) dias a 1 (um) ano</li>
            </ul>
            <p className="mb-4">
              Durante a suspensão, você não terá acesso aos serviços, mas sua conta e dados serão mantidos. Após o período de suspensão, o acesso pode ser restaurado mediante revisão e aprovação da FabChat.
            </p>
            <p className="mb-4">
              <strong>BANIMENTO PERMANENTE:</strong> A FabChat pode banir permanentemente sua conta e acesso aos serviços em casos de:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Exploração de bugs, glitches ou vulnerabilidades</li>
              <li>Uso não autorizado de recursos premium</li>
              <li>Manipulação de sistemas de segurança, autenticação ou cobrança</li>
              <li>Fraude, estelionato informático ou crimes relacionados</li>
              <li>Violações repetidas após suspensões anteriores</li>
              <li>Prejuízo financeiro significativo à FabChat</li>
              <li>Compartilhamento ou distribuição de métodos de exploração</li>
              <li>Qualquer violação que constitua crime conforme legislação brasileira</li>
            </ul>
            <p className="mb-4">
              <strong>BLOQUEIO DE IP E DISPOSITIVOS:</strong> Além do banimento da conta, a FabChat pode:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Bloquear permanentemente endereços IP associados à violação</li>
              <li>Bloquear dispositivos, navegadores ou aplicativos utilizados</li>
              <li>Implementar medidas técnicas para prevenir criação de novas contas</li>
              <li>Compartilhar informações com serviços de segurança e prevenção de fraude</li>
            </ul>
            <p className="mb-4">
              <strong>CANCELAMENTO DE ASSINATURA SEM REEMBOLSO:</strong> Em caso de violação grave, a FabChat pode:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Cancelar imediatamente sua assinatura sem direito a reembolso</li>
              <li>Reter valores já pagos como compensação parcial por danos</li>
              <li>Cobrar valores adicionais devidos por uso não autorizado</li>
            </ul>
            <p className="mb-4">
              <strong>RETENÇÃO DE DADOS PARA FINS LEGAIS:</strong> A FabChat pode reter seus dados, logs, histórico de uso e evidências de violação por período necessário para:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Investigação e comprovação de violações</li>
              <li>Processos judiciais ou administrativos</li>
              <li>Cumprimento de obrigações legais e regulatórias</li>
              <li>Prevenção de futuras violações</li>
            </ul>
            <p className="mb-4">
              <strong>NOTIFICAÇÃO A AUTORIDADES:</strong> A FabChat se reserva o direito de notificar e cooperar com:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Autoridades policiais competentes</li>
              <li>Órgãos de proteção ao consumidor (PROCON, ANPD)</li>
              <li>Autoridades judiciárias</li>
              <li>Serviços de prevenção de fraude e segurança cibernética</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">5.5. Multas e Compensação Financeira</h3>
            <p className="mb-4">
              <strong>MULTA POR USO INDEVIDO DE RECURSOS PREMIUM:</strong> Além da cobrança retroativa de valores devidos, você será obrigado a pagar:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Multa administrativa:</strong> Valor equivalente a 10% (dez por cento) a 20% (vinte por cento) sobre o valor total que deveria ter sido pago pelo uso não autorizado</li>
              <li><strong>Cálculo baseado em uso:</strong> O valor será calculado com base no plano premium correspondente aos recursos utilizados, multiplicado pelo período de uso não autorizado</li>
              <li><strong>Tarifa de plano superior:</strong> Se recursos de planos superiores foram acessados, será aplicada a tarifa do plano mais alto utilizado</li>
            </ul>
            <p className="mb-4">
              <strong>COMPENSAÇÃO POR DANOS CAUSADOS À PLATAFORMA:</strong> Você será responsável por indenizar a FabChat por:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Custos de investigação técnica e forense</li>
              <li>Desenvolvimento e implementação de correções de segurança</li>
              <li>Perda de receita decorrente de exploração de vulnerabilidades</li>
              <li>Danos à reputação e imagem da plataforma</li>
              <li>Custos de notificação a usuários afetados (quando aplicável)</li>
              <li>Honorários advocatícios e custas processuais</li>
            </ul>
            <p className="mb-4">
              <strong>REEMBOLSO DE CUSTOS DE INVESTIGAÇÃO E CORREÇÃO:</strong> Todos os custos relacionados à investigação, correção e prevenção de violações serão cobrados do infrator, incluindo:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Horas de trabalho de equipes técnicas e de segurança</li>
              <li>Custos de ferramentas e serviços de segurança utilizados</li>
              <li>Desenvolvimento de patches e correções de segurança</li>
              <li>Testes e validações de segurança</li>
            </ul>
            <p className="mb-4">
              <strong>JUROS E CORREÇÃO MONETÁRIA:</strong> Valores em atraso serão cobrados com:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Juros de mora de 1% (um por cento) ao mês</li>
              <li>Correção monetária pelo IPCA-E ou índice equivalente</li>
              <li>Multa de 2% (dois por cento) sobre o valor devido</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">5.6. Processo de Apelação e Revisão</h3>
            <p className="mb-4">
              <strong>DIREITO DE CONTESTAÇÃO:</strong> Você tem o direito de contestar medidas disciplinares aplicadas, mediante:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Prazo:</strong> 15 (quinze) dias corridos a partir da notificação da medida disciplinar</li>
              <li><strong>Canal:</strong> Envio de e-mail para atendimento.fabtech@gmail.com com assunto "Apelação - [ID da Conta]"</li>
              <li><strong>Documentação obrigatória:</strong> Identificação completa, justificativa detalhada, evidências e documentos comprobatórios</li>
            </ul>
            <p className="mb-4">
              <strong>PROCESSO DE REVISÃO:</strong> A FabChat realizará revisão da decisão considerando:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Evidências técnicas e logs do sistema</li>
              <li>Argumentos e documentação apresentados pelo usuário</li>
              <li>Gravidade e natureza da violação alegada</li>
              <li>Histórico de violações anteriores</li>
              <li>Impacto e prejuízos causados</li>
            </ul>
            <p className="mb-4">
              <strong>PRAZO DE RESPOSTA:</strong> A FabChat se compromete a responder à apelação em até 30 (trinta) dias úteis após o recebimento completo da documentação.
            </p>
            <p className="mb-4">
              <strong>CRITÉRIOS PARA REVERSÃO DE BANIMENTO:</strong> A reversão de banimento permanente é excepcional e pode ocorrer apenas se:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Ficar comprovado erro técnico ou humano na detecção da violação</li>
              <li>A violação alegada não se configurar conforme evidências apresentadas</li>
              <li>Ocorrer acordo de compensação e compromisso de não reincidência</li>
            </ul>
            <p className="mb-4">
              <strong>LIMITE DE APELAÇÃO:</strong> Apenas uma apelação será aceita por violação. Decisões sobre apelações são finais e não cabem recursos adicionais, exceto via processo judicial.
            </p>

            <h3 className="text-xl font-semibold mb-3">5.7. Medidas Preventivas e Monitoramento</h3>
            <p className="mb-4">
              <strong>DIREITO DE MONITORAMENTO:</strong> A FabChat se reserva o direito de:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Monitorar continuamente o uso da plataforma por todos os usuários</li>
              <li>Analisar padrões de uso, comportamento e acesso</li>
              <li>Implementar sistemas automatizados de detecção de anomalias</li>
              <li>Registrar e armazenar logs de todas as atividades e interações</li>
              <li>Realizar auditorias técnicas e de segurança</li>
            </ul>
            <p className="mb-4">
              <strong>DETECÇÃO AUTOMÁTICA DE PADRÕES SUSPEITOS:</strong> A plataforma utiliza sistemas automatizados que detectam:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Uso anormal de recursos ou limites</li>
              <li>Padrões de acesso inconsistentes com o plano contratado</li>
              <li>Tentativas de bypass de sistemas de segurança</li>
              <li>Comportamentos que indicam exploração de bugs ou vulnerabilidades</li>
              <li>Múltiplas contas associadas ao mesmo usuário ou dispositivo</li>
            </ul>
            <p className="mb-4">
              <strong>ANÁLISE DE LOGS E DADOS DE USO:</strong> A FabChat pode analisar:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Logs de acesso, autenticação e autorização</li>
              <li>Histórico de uso de recursos e funcionalidades</li>
              <li>Padrões de requisições e respostas de API</li>
              <li>Dados de assinatura, pagamento e cobrança</li>
              <li>Metadados de dispositivos, IPs e navegadores</li>
            </ul>
            <p className="mb-4">
              <strong>COOPERAÇÃO COM INVESTIGAÇÕES:</strong> A FabChat cooperará com:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Investigações internas de segurança e conformidade</li>
              <li>Investigações de autoridades competentes (mediante ordem judicial)</li>
              <li>Serviços de prevenção de fraude e segurança cibernética</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">5.8. Responsabilidades Legais e Indenização</h3>
            <p className="mb-4">
              <strong>VIOLAÇÃO PODE CONSTITUIR CRIME:</strong> As condutas descritas nesta seção podem configurar crimes conforme legislação brasileira, incluindo:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Art. 171-A do Código Penal (Estelionato Informático):</strong> Obter vantagem ilícita através de manipulação de sistema de processamento de dados ou interferência não autorizada</li>
              <li><strong>Art. 155 do Código Penal (Furto):</strong> Quando há apropriação indevida de serviços ou recursos</li>
              <li><strong>Art. 163 do Código Penal (Dano):</strong> Quando há prejuízo à plataforma ou outros usuários</li>
              <li><strong>Art. 154-A do Código Penal (Invasão de Dispositivo Informático):</strong> Quando há acesso não autorizado a sistemas</li>
            </ul>
            <p className="mb-4">
              <strong>RESPONSABILIDADE CIVIL POR DANOS:</strong> Além das responsabilidades criminais, você será responsável civilmente por:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Danos materiais causados à FabChat (perda de receita, custos de correção, etc.)</li>
              <li>Danos morais à imagem e reputação da FabChat</li>
              <li>Danos a terceiros decorrentes de suas ações</li>
              <li>Lucros cessantes e danos emergentes</li>
            </ul>
            <p className="mb-4">
              <strong>OBRIGAÇÃO DE INDENIZAR FABCHAT:</strong> Você se compromete a indenizar a FabChat por:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Todos os valores devidos por uso não autorizado</li>
              <li>Multas e penalidades aplicadas</li>
              <li>Custos de investigação, correção e prevenção</li>
              <li>Honorários advocatícios e custas processuais</li>
              <li>Danos materiais e morais comprovados</li>
            </ul>
            <p className="mb-4">
              <strong>POSSIBILIDADE DE AÇÃO JUDICIAL:</strong> A FabChat se reserva o direito de:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Propor ações judiciais para recuperação de valores e indenização</li>
              <li>Notificar autoridades competentes sobre crimes cometidos</li>
              <li>Buscar medidas cautelares para prevenir continuidade de violações</li>
              <li>Exigir cumprimento de obrigações através de execução judicial</li>
            </ul>
            <p className="mb-4">
              <strong>NOTIFICAÇÃO A AUTORIDADES COMPETENTES:</strong> A FabChat notificará autoridades quando:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Houver indícios de crime conforme legislação brasileira</li>
              <li>O prejuízo financeiro for significativo (acima de R$ 5.000,00)</li>
              <li>Houver risco à segurança de outros usuários ou da plataforma</li>
              <li>For exigido por lei ou ordem judicial</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">5.9. Efeitos do Banimento</h3>
            <p className="mb-4">
              <strong>PERDA IMEDIATA DE ACESSO:</strong> Com o banimento, você perderá imediatamente:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Acesso a todos os serviços e funcionalidades da plataforma</li>
              <li>Acesso à sua conta, dados e configurações</li>
              <li>Acesso a recursos premium, mesmo que tenha assinatura ativa</li>
              <li>Capacidade de criar, editar ou gerenciar qualquer conteúdo</li>
            </ul>
            <p className="mb-4">
              <strong>PERDA DE DADOS E CONTEÚDO:</strong> Após período de retenção legal (conforme Seção 15.12 - DPA), seus dados serão:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Excluídos permanentemente da plataforma</li>
              <li>Não recuperáveis após a exclusão</li>
              <li>Mantidos apenas quando necessário para fins legais ou investigações</li>
            </ul>
            <p className="mb-4">
              <strong>PROIBIÇÃO DE CRIAR NOVAS CONTAS:</strong> Você está proibido de:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Criar novas contas na plataforma FabChat</li>
              <li>Utilizar contas de terceiros para acessar a plataforma</li>
              <li>Solicitar que outras pessoas criem contas em seu nome</li>
              <li>Utilizar métodos para contornar bloqueios (VPNs, proxies, etc.)</li>
            </ul>
            <p className="mb-4">
              <strong>BLOQUEIO DE MÉTODOS DE PAGAMENTO:</strong> A FabChat pode:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Bloquear permanentemente métodos de pagamento associados à sua conta banida</li>
              <li>Recusar processamento de pagamentos de contas relacionadas</li>
              <li>Compartilhar informações com processadores de pagamento para prevenção de fraude</li>
            </ul>
            <p className="mb-4">
              <strong>DIVULGAÇÃO DE IDENTIDADE EM CASOS GRAVES:</strong> Em casos de violações graves que constituam crimes, a FabChat pode:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Divulgar informações de identidade para autoridades competentes</li>
              <li>Fornecer evidências e documentação para processos judiciais</li>
              <li>Cooperar com investigações criminais</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">5.10. Casos Especiais e Agravantes</h3>
            <p className="mb-4">
              <strong>VIOLAÇÕES REPETIDAS:</strong> Violações repetidas resultarão em:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Penalidades progressivamente mais severas</li>
              <li>Redução ou eliminação de períodos de suspensão (banimento imediato)</li>
              <li>Multas e compensações aumentadas</li>
              <li>Maior probabilidade de ações legais</li>
            </ul>
            <p className="mb-4">
              <strong>VIOLAÇÕES EM LARGA ESCALA:</strong> Violações que afetem múltiplos usuários, sistemas ou causem prejuízos significativos resultarão em:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Banimento permanente imediato</li>
              <li>Multas e compensações proporcionais ao dano causado</li>
              <li>Notificação imediata a autoridades competentes</li>
              <li>Ações legais prioritárias</li>
            </ul>
            <p className="mb-4">
              <strong>PREJUÍZO FINANCEIRO SIGNIFICATIVO À FABCHAT:</strong> Quando o prejuízo financeiro for superior a R$ 5.000,00 (cinco mil reais), resultará em:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Banimento permanente imediato</li>
              <li>Cobrança do valor total do prejuízo, acrescido de multa de 20% (vinte por cento)</li>
              <li>Notificação obrigatória a autoridades competentes</li>
              <li>Ação judicial para recuperação de valores e indenização</li>
            </ul>
            <p className="mb-4">
              <strong>COMPARTILHAMENTO DE MÉTODOS DE EXPLORAÇÃO:</strong> Compartilhar, ensinar, vender ou distribuir métodos de exploração resultará em:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Banimento permanente imediato</li>
              <li>Responsabilização por danos causados por terceiros que utilizaram os métodos compartilhados</li>
              <li>Multa adicional de até R$ 10.000,00 (dez mil reais)</li>
              <li>Notificação a autoridades para investigação de possível formação de quadrilha</li>
            </ul>
            <p className="mb-4">
              <strong>CRIAÇÃO DE FERRAMENTAS PARA EXPLORAÇÃO:</strong> Desenvolver, distribuir ou vender ferramentas para exploração resultará em:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Banimento permanente imediato</li>
              <li>Responsabilização por todos os danos causados por usuários das ferramentas</li>
              <li>Multa de até R$ 50.000,00 (cinquenta mil reais)</li>
              <li>Notificação obrigatória a autoridades para investigação criminal</li>
              <li>Ação judicial para proibição de continuidade das atividades e indenização</li>
            </ul>
            <p className="mb-4">
              <strong>DISPOSIÇÕES FINAIS:</strong> Esta seção se aplica a todas as violações, independentemente de serem detectadas imediatamente ou posteriormente. A FabChat se reserva o direito de aplicar medidas disciplinares retroativas quando violações forem descobertas, mesmo após período considerável de tempo.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Integrações com Plataformas Terceiras e Responsabilidade do Cliente</h2>
            
            <h3 className="text-xl font-semibold mb-3">6.1. Natureza das Integrações</h3>
            <p className="mb-4">
              A FabChat oferece integração com plataformas de terceiros, incluindo mas não limitado a:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>WhatsApp Business API (Meta Platforms Inc.)</li>
              <li>Instagram API (Meta Platforms Inc.)</li>
              <li>Google Calendar API (Google LLC)</li>
              <li>Outras plataformas que possam ser integradas no futuro</li>
            </ul>
            <p className="mb-4">
              <strong>IMPORTANTE:</strong> A FabChat atua exclusivamente como intermediária técnica, fornecendo uma plataforma que permite ao Cliente conectar suas próprias contas e credenciais de plataformas terceiras. A FabChat não possui, controla ou gerencia as contas do Cliente nessas plataformas.
            </p>

            <h3 className="text-xl font-semibold mb-3">6.2. Responsabilidade do Cliente pelas Contas e Credenciais</h3>
            <p className="mb-4">
              O Cliente é <strong>exclusiva e integralmente responsável</strong> por:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Obter e manter contas válidas e autorizadas nas plataformas terceiras</li>
              <li>Gerenciar suas próprias credenciais de acesso (tokens, chaves de API, etc.)</li>
              <li>Cumprir rigorosamente os Termos de Serviço, Políticas de Uso e Diretrizes de cada plataforma integrada</li>
              <li>Garantir que possui autorização legal e contratual para utilizar as APIs e serviços das plataformas terceiras</li>
              <li>Manter-se atualizado sobre mudanças nas políticas e termos das plataformas terceiras</li>
              <li>Respeitar limites de uso, rate limits e políticas de anti-spam de cada plataforma</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">6.3. Banimentos e Suspensões em Plataformas Terceiras</h3>
            <p className="mb-4">
              <strong>ISENÇÃO TOTAL DE RESPONSABILIDADE:</strong> A FabChat <strong>NÃO se responsabiliza</strong>, em hipótese alguma, por:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Banimentos, suspensões ou restrições de contas do Cliente em plataformas terceiras (WhatsApp, Instagram, Google, etc.)</li>
              <li>Perda de acesso a contas ou funcionalidades em plataformas terceiras</li>
              <li>Danos, prejuízos ou perdas de receita decorrentes de banimentos ou suspensões</li>
              <li>Interrupção de serviços causada por ações das plataformas terceiras</li>
              <li>Mudanças nas políticas, APIs ou termos de serviço das plataformas terceiras</li>
            </ul>
            <p className="mb-4">
              O Cliente reconhece e aceita que plataformas terceiras podem, a seu exclusivo critério e sem aviso prévio:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Suspender ou banir contas que violem seus termos de serviço</li>
              <li>Alterar políticas, APIs ou funcionalidades disponíveis</li>
              <li>Limitar ou restringir acesso a APIs ou serviços</li>
              <li>Tomar medidas disciplinares contra contas que considerem em violação</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">6.4. Uso Indevido e Violação de Políticas</h3>
            <p className="mb-4">
              O Cliente é <strong>exclusivamente responsável</strong> por garantir que seu uso da plataforma FabChat, em conjunto com as plataformas terceiras, esteja em total conformidade com:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Termos de Serviço do WhatsApp Business API e Políticas de Uso do WhatsApp</li>
              <li>Termos de Serviço do Instagram e Políticas da Comunidade do Instagram</li>
              <li>Termos de Serviço do Google Calendar e Políticas do Google</li>
              <li>Leis e regulamentos brasileiros aplicáveis (LGPD, Marco Civil da Internet, CDC, etc.)</li>
              <li>Leis e regulamentos internacionais aplicáveis</li>
            </ul>
            <p className="mb-4">
              <strong>EXEMPLOS DE USO INDEVIDO (não exaustivo):</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Envio de mensagens não solicitadas (spam) via WhatsApp ou Instagram</li>
              <li>Uso de bots ou automações em violação às políticas das plataformas</li>
              <li>Envio de conteúdo abusivo, ofensivo, discriminatório ou ilegal</li>
              <li>Coleta não autorizada de dados pessoais</li>
              <li>Uso de contas não verificadas ou não autorizadas</li>
              <li>Exceder limites de taxa (rate limits) estabelecidos pelas plataformas</li>
              <li>Uso de APIs de forma não autorizada ou em violação aos termos</li>
              <li>Compartilhamento não autorizado de credenciais ou tokens</li>
            </ul>
            <p className="mb-4">
              <strong>CONSEQUÊNCIAS DO USO INDEVIDO:</strong> Em caso de detecção de uso indevido ou violação de políticas de plataformas terceiras:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>A FabChat pode suspender ou encerrar imediatamente o acesso do Cliente à plataforma</li>
              <li>O Cliente será responsável por todos os danos, prejuízos e custos decorrentes</li>
              <li>A FabChat não será responsável por perda de dados, receita ou oportunidades de negócio</li>
              <li>O Cliente deve indenizar a FabChat por quaisquer danos causados</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">6.5. Monitoramento e Conformidade</h3>
            <p className="mb-4">
              Embora a FabChat possa implementar medidas técnicas para auxiliar na conformidade, o Cliente reconhece que:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>A responsabilidade final pela conformidade com políticas de plataformas terceiras é exclusiva do Cliente</li>
              <li>A FabChat não garante que o uso da plataforma sempre estará em conformidade com todas as políticas de terceiros</li>
              <li>O Cliente deve monitorar ativamente seu próprio uso e garantir conformidade contínua</li>
              <li>Mudanças nas políticas de terceiros podem afetar a funcionalidade, e a FabChat não se responsabiliza por tais mudanças</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">6.6. Limitação de Responsabilidade Específica</h3>
            <p className="mb-4">
              <strong>NA MÁXIMA EXTENSÃO PERMITIDA POR LEI:</strong> A FabChat não será responsável, em hipótese alguma, por:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Banimentos, suspensões ou restrições impostas por plataformas terceiras</li>
              <li>Perda de receita, clientes ou oportunidades de negócio decorrentes de banimentos</li>
              <li>Danos indiretos, incidentais, consequenciais ou lucros cessantes relacionados a plataformas terceiras</li>
              <li>Interrupção de serviços causada por ações de plataformas terceiras</li>
              <li>Mudanças em APIs, políticas ou termos de serviço de plataformas terceiras</li>
              <li>Falhas técnicas ou indisponibilidade de serviços de plataformas terceiras</li>
            </ul>
            <p className="mb-4">
              Esta limitação se aplica mesmo que a FabChat tenha sido avisada sobre a possibilidade de tais danos e independentemente da teoria legal (contrato, delito, responsabilidade objetiva, etc.) sob a qual a ação seja proposta.
            </p>

            <h3 className="text-xl font-semibold mb-3">6.7. Responsabilidade por Respostas da Inteligência Artificial</h3>
            <p className="mb-4">
              <strong>ISENÇÃO DE RESPONSABILIDADE POR CONTEÚDO GERADO POR IA:</strong> A FabChat fornece uma plataforma tecnológica que utiliza inteligência artificial de terceiros (incluindo mas não limitado a OpenAI) para processar e gerar respostas automáticas. O Cliente reconhece e aceita que:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>A FabChat atua exclusivamente como intermediária técnica, fornecendo acesso a tecnologia de IA</li>
              <li>O Cliente permanece responsável pelo conteúdo das respostas geradas pelos agentes de IA e pela validação das configurações, observados os Serviços Profissionais eventualmente contratados e a Seção 6.8</li>
              <li>A FabChat não revisa, edita, aprova ou controla, em caráter contínuo, o conteúdo das respostas geradas pela IA após a disponibilização da plataforma ou a conclusão de eventual implementação</li>
              <li>As respostas são geradas automaticamente com base nas configurações, instruções, materiais e regras de negócio fornecidos ou aprovados pelo Cliente</li>
            </ul>

            <p className="mb-4">
              <strong>ISENÇÃO TOTAL DE RESPONSABILIDADE:</strong> A FabChat <strong>NÃO se responsabiliza</strong>, em hipótese alguma, por:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Respostas interpretadas incorretamente ou enviadas de forma errônea pela IA</li>
              <li>Conteúdo inadequado, ofensivo, discriminatório ou ilegal gerado pela IA</li>
              <li>Informações incorretas, imprecisas ou enganosas transmitidas pela IA</li>
              <li>Promessas, compromissos ou garantias feitas pela IA que não possam ser cumpridas</li>
              <li>Danos, prejuízos ou perdas decorrentes de respostas da IA, incluindo mas não limitado a:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Perda de clientes ou oportunidades de negócio</li>
                  <li>Danos à reputação ou imagem da empresa do Cliente</li>
                  <li>Responsabilização civil, trabalhista ou comercial</li>
                  <li>Multas, sanções ou penalidades aplicadas por autoridades</li>
                  <li>Processos judiciais ou administrativos</li>
                </ul>
              </li>
              <li>Falhas na interpretação de intenções do cliente ou contexto da conversa</li>
              <li>Erros na compreensão de perguntas ou solicitações dos clientes</li>
              <li>Limitações técnicas ou comportamentais da tecnologia de IA utilizada</li>
            </ul>

            <p className="mb-4">
              <strong>RESPONSABILIDADE DO CLIENTE:</strong> O Cliente é <strong>exclusivamente responsável</strong> por:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Configurar, revisar e validar os agentes de IA conforme a Seção 6.8, inclusive quando a FabChat participar da implementação por meio de Serviços Profissionais</li>
              <li>Revisar e testar as configurações antes de ativar os agentes em produção</li>
              <li>Monitorar ativamente as conversas e respostas geradas pela IA</li>
              <li>Corrigir ou ajustar configurações quando identificar problemas ou respostas inadequadas</li>
              <li>Garantir que as instruções e configurações estejam em conformidade com leis aplicáveis</li>
              <li>Garantir que o conteúdo gerado pela IA esteja alinhado com os valores, políticas e práticas da empresa do Cliente</li>
              <li>Implementar medidas de controle e supervisão adequadas sobre o uso da IA</li>
              <li>Responder por todos os danos, prejuízos e responsabilidades decorrentes de respostas da IA</li>
              <li>Indenizar a FabChat por quaisquer reivindicações, processos ou danos relacionados a respostas da IA</li>
            </ul>

            <p className="mb-4">
              <strong>NATUREZA DA TECNOLOGIA:</strong> O Cliente reconhece que:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>A tecnologia de inteligência artificial pode apresentar limitações, imprecisões ou comportamentos inesperados</li>
              <li>As respostas são geradas probabilisticamente e podem variar mesmo com configurações idênticas</li>
              <li>A IA pode interpretar contextos de forma diferente do esperado pelo Cliente</li>
              <li>Não há garantia de que todas as respostas serão sempre precisas, adequadas ou livres de erros</li>
              <li>A FabChat não garante resultados específicos ou desempenho particular da IA</li>
              <li>O Cliente deve implementar supervisão humana adequada quando necessário</li>
            </ul>

            <p className="mb-4">
              <strong>LIMITAÇÃO DE RESPONSABILIDADE:</strong> Na máxima extensão permitida por lei, a FabChat não será responsável, em hipótese alguma, por danos diretos, indiretos, incidentais, consequenciais, lucros cessantes ou danos morais decorrentes de:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Respostas, interpretações ou conteúdo gerado pela IA</li>
              <li>Uso ou confiança em informações fornecidas pela IA</li>
              <li>Decisões tomadas com base em respostas da IA</li>
              <li>Falhas, erros ou limitações da tecnologia de IA</li>
            </ul>
            <p className="mb-4">
              Esta isenção se aplica independentemente da teoria legal (contrato, delito, responsabilidade objetiva, responsabilidade pelo fato do produto, etc.) sob a qual a ação seja proposta, e mesmo que a FabChat tenha sido avisada sobre a possibilidade de tais danos.
            </p>

            <h3 className="text-xl font-semibold mb-3">6.8. Responsabilidade pela Configuração</h3>
            <p className="mb-4">
              Salvo quando houver contratação expressa de Serviços Profissionais que incluam atividades de configuração ou implementação pela FabChat, o Cliente é responsável pela configuração e personalização de seus agentes de IA, canais e funis, incluindo prompts, instruções, regras de negócio, produtos, tom de voz e demais parâmetros que influenciam seu comportamento.
            </p>
            <p className="mb-4">
              Quando a FabChat participar da configuração ou implementação em razão da contratação de Serviços Profissionais, a configuração será realizada com base nas informações, instruções, materiais e regras de negócio fornecidos ou aprovados pelo Cliente.
            </p>
            <p className="mb-4">
              Mesmo nesses casos, caberá ao Cliente revisar e validar as configurações e, quando aplicável, aprová-las antes da utilização em produção, bem como monitorar posteriormente o comportamento dos agentes e solicitar ou realizar os ajustes necessários.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Dados e Conteúdo do Usuário</h2>
            
            <h3 className="text-xl font-semibold mb-3">7.1. Propriedade dos Dados</h3>
            <p className="mb-4">
              Você mantém todos os direitos de propriedade sobre seus dados, incluindo conversas, leads, configurações de agentes e outros conteúdos que você cria ou carrega na plataforma.
            </p>

            <h3 className="text-xl font-semibold mb-3">7.2. Licença de Uso</h3>
            <p className="mb-4">
              Ao utilizar nossos serviços, você nos concede uma licença limitada para acessar, usar, processar e armazenar seus dados exclusivamente para:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Fornecer e melhorar nossos serviços</li>
              <li>Processar conversas e automatizar atendimento conforme configurado</li>
              <li>Cumprir obrigações legais e regulatórias</li>
              <li>Garantir a segurança e integridade dos serviços</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">7.3. Responsabilidade pelos Dados de Clientes</h3>
            <p className="mb-4">
              Você é responsável por garantir que possui base legal adequada (consentimento, legítimo interesse ou outra base legal válida) para processar os dados pessoais de seus clientes através de nossa plataforma, conforme exigido pela LGPD e outras leis aplicáveis.
            </p>
            <p className="mb-4">
              Você declara e garante que:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Possui autorização adequada para processar dados de clientes através dos canais integrados</li>
              <li>Está em conformidade com os termos de serviço das plataformas integradas (WhatsApp, Instagram, etc.)</li>
              <li>Respeita os direitos de privacidade e proteção de dados de seus clientes</li>
              <li>Não utilizará nossos serviços para coletar ou processar dados de forma ilegal ou não autorizada</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Propriedade Intelectual</h2>
            
            <h3 className="text-xl font-semibold mb-3">8.1. Propriedade da FabChat</h3>
            <p className="mb-4">
              A FabChat é titular exclusiva de todos os direitos de propriedade intelectual sobre a plataforma, incluindo mas não limitado a: software, código-fonte, interface, design, marca "FabChat", logotipos, documentação, textos, gráficos e demais materiais disponibilizados nos serviços. Nada nestes Termos transfere ou concede a você qualquer direito de propriedade sobre a plataforma ou sobre a marca FabChat.
            </p>
            
            <h3 className="text-xl font-semibold mb-3">8.2. Licença de Uso Limitada</h3>
            <p className="mb-4">
              Durante a vigência do contrato e mediante o cumprimento destes Termos, a FabChat concede a você uma licença limitada, não exclusiva, intransferível e revogável para acessar e utilizar a plataforma exclusivamente para seus fins comerciais internos, na forma prevista nestes Termos. Esta licença <strong>não</strong> inclui direito a sublicenciar, revender, distribuir, modificar, fazer engenharia reversa, descompilar ou desmontar a plataforma, nem utilizar a marca FabChat sem autorização prévia por escrito.
            </p>
            
            <h3 className="text-xl font-semibold mb-3">8.3. Conteúdo Gerado pelo Usuário</h3>
            <p className="mb-4">
              Você mantém a titularidade sobre o conteúdo que criar ou carregar na plataforma (conversas, leads, configurações de agentes, textos, imagens e demais dados — "Conteúdo do Usuário"). Ao utilizar os serviços, você concede à FabChat uma licença mundial, não exclusiva, isenta de royalties e sublicenciável para usar, armazenar, processar, reproduzir e exibir o Conteúdo do Usuário exclusivamente para fornecer, operar, melhorar e promover os serviços, em conformidade com nossa Política de Privacidade e com o DPA (Seção 15).
            </p>
            <p className="mb-4">
              Você declara e garante que possui todos os direitos necessários sobre o Conteúdo do Usuário e que esse conteúdo não viola direitos de terceiros nem leis aplicáveis.
            </p>
            
            <h3 className="text-xl font-semibold mb-3">8.4. Restrições</h3>
            <p className="mb-4">É expressamente proibido, sem autorização prévia por escrito da FabChat:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Copiar, modificar ou criar obras derivadas da plataforma ou de qualquer parte dela</li>
              <li>Realizar engenharia reversa, descompilação ou desmontagem do software</li>
              <li>Utilizar a marca, logotipos ou identidade visual da FabChat de forma que sugira associação ou endosso não autorizado</li>
              <li>Remover ou alterar avisos de direitos autorais ou de propriedade intelectual</li>
              <li>Utilizar a plataforma para desenvolver produto ou serviço concorrente</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              9. Assinaturas, Pagamentos, Inadimplência, Suspensão, Cancelamento e Serviços Profissionais
            </h2>

            <h3 className="text-xl font-semibold mb-3">9.1. Planos, Preços, Funcionalidades e Limites</h3>
            <p className="mb-4">
              A Plataforma oferece diferentes planos de assinatura, com funcionalidades, limites, recursos, integrações, volume de uso, automações e preços próprios, conforme informado na página de planos, no checkout, na proposta comercial ou no painel da conta.
            </p>
            <p className="mb-4">
              Os preços, funcionalidades, limites, condições promocionais e recursos de cada plano poderão ser alterados, atualizados, substituídos ou descontinuados a qualquer momento, mediante comunicação ou disponibilização das novas condições nos canais oficiais da Plataforma, respeitado o período já pago pelo usuário, quando aplicável.
            </p>
            <p className="mb-4">
              O uso de funcionalidades acima dos limites contratados poderá exigir upgrade de plano, contratação adicional, pagamento de valores complementares ou limitação automática de recursos.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.2. Cobrança e Processamento de Pagamentos</h3>
            <p className="mb-4">
              Os pagamentos são processados por provedores de pagamento terceirizados, incluindo, mas não se limitando à Stripe. Ao contratar um plano, o usuário declara que as informações de pagamento fornecidas são verdadeiras, completas e atualizadas, e autoriza a cobrança dos valores devidos conforme o plano contratado, periodicidade escolhida, tributos aplicáveis, encargos e eventuais valores adicionais.
            </p>
            <p className="mb-4">
              A confirmação, compensação, recusa, atraso, chargeback, cancelamento ou falha de pagamento poderá depender do provedor de pagamento, instituição financeira, bandeira de cartão, banco emissor, sistema antifraude ou prazo de compensação bancária.
            </p>
            <p className="mb-4">
              A Plataforma poderá considerar como válido, para fins de ativação, suspensão, reativação ou cancelamento da assinatura, o status informado pelo provedor de pagamento, incluindo, sem limitação, pagamento pendente, pagamento recusado, pagamento vencido, pagamento em análise, past_due, unpaid, canceled, chargeback ou status equivalente.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.3. Renovação Automática</h3>
            <p className="mb-4">
              Salvo disposição em contrário, as assinaturas são renovadas automaticamente ao final de cada ciclo de cobrança, até que sejam canceladas pelo usuário ou encerradas pela Plataforma nos termos deste instrumento.
            </p>
            <p className="mb-4">
              Nos <strong>Planos Anuais Parcelados</strong>, cada cobrança mensal corresponde a uma parcela do compromisso de 12 meses (Seção 9.12), e não a um novo contrato mensal independente. A renovação automática após o período anual seguirá as regras da Seção 9.12.
            </p>
            <p className="mb-4">
              O usuário é responsável por manter método de pagamento válido, limite disponível, saldo suficiente, boleto quitado dentro do vencimento e dados cadastrais atualizados para evitar falhas de cobrança, inadimplência, suspensão ou cancelamento da assinatura.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.4. Inadimplência e Falha de Pagamento</h3>
            <p className="mb-4">
              O não pagamento de qualquer valor devido até a respectiva data de vencimento, a ausência de compensação de boleto, a falha na cobrança automática, a recusa do cartão, a insuficiência de saldo ou limite, o chargeback, a contestação indevida, o cancelamento do pagamento, a suspeita de fraude ou qualquer inconsistência no processamento poderá caracterizar inadimplência.
            </p>
            <p className="mb-4">
              Em caso de inadimplência, a Plataforma poderá, a seu exclusivo critério e sem prejuízo de outras medidas cabíveis:
            </p>
            <ol className="mb-4 list-[upper-roman] pl-8 space-y-3 [list-style-position:outside]">
              <li className="pl-1">manter a conta ativa por prazo de tolerância, se houver;</li>
              <li className="pl-1">restringir, limitar ou suspender parcial ou totalmente o acesso à Plataforma;</li>
              <li className="pl-1">
                bloquear funcionalidades, integrações, automações, relatórios, APIs, disparos, consultas, exportações operacionais, publicações ou demais recursos do plano;
              </li>
              <li className="pl-1">
                suspender produtos gratuitos, bônus, créditos promocionais, benefícios ou recursos adicionais vinculados à conta inadimplente;
              </li>
              <li className="pl-1">impedir novas operações até a regularização do pagamento;</li>
              <li className="pl-1">cancelar a assinatura;</li>
              <li className="pl-1">encerrar benefícios, descontos, condições promocionais ou valores diferenciados;</li>
              <li className="pl-1">
                cobrar valores vencidos, encargos, multa, juros, correção monetária, tributos e demais valores permitidos pela legislação aplicável;
              </li>
              <li className="pl-1">adotar medidas de cobrança administrativa, extrajudicial ou judicial.</li>
            </ol>

            <h3 className="text-xl font-semibold mb-3">9.5. Suspensão Automática do Serviço</h3>
            <p className="mb-4">
              O usuário reconhece e concorda que o acesso à Plataforma poderá ser suspenso automaticamente caso o pagamento não seja identificado, confirmado ou compensado até a data de vencimento.
            </p>
            <p className="mb-4">
              A suspensão poderá ocorrer de forma automática com base nas informações fornecidas pelo provedor de pagamento, inclusive quando a assinatura, fatura ou cobrança estiver com status de pagamento pendente, vencido, recusado, falho, em atraso, past_due, unpaid, canceled, chargeback ou equivalente.
            </p>
            <p className="mb-4">
              A suspensão poderá afetar a totalidade da conta, incluindo funcionalidades pagas, recursos gratuitos, bônus, integrações, automações, canais conectados, agentes, fluxos, relatórios, APIs, disparos, consultas e demais recursos relacionados à assinatura.
            </p>
            <p className="mb-4">
              A Plataforma poderá enviar avisos, lembretes ou notificações de cobrança antes ou depois do vencimento, por e-mail, painel interno, WhatsApp, SMS ou outros canais informados pelo usuário. Tais comunicações terão caráter de cortesia e não constituirão condição obrigatória para a suspensão, restrição ou cancelamento do serviço, desde que a regra de inadimplência esteja prevista nestes Termos.
            </p>
            <p className="mb-4">
              Sempre que comercialmente razoável, a Plataforma poderá conceder oportunidade de regularização antes do cancelamento definitivo da assinatura, sem que isso represente renúncia de direitos, novação contratual ou obrigação de concessão do mesmo prazo em situações futuras.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.6. Prazo de Tolerância</h3>
            <p className="mb-4">
              Na ausência de regra específica em contrário, a Plataforma poderá suspender o acesso a partir da data de vencimento não paga ou da falha de cobrança, sem prejuízo da possibilidade de concessão de prazo de tolerância por mera liberalidade.
            </p>
            <p className="mb-4">
              A concessão de prazo de tolerância, reativação temporária, tentativa adicional de cobrança, prorrogação de acesso ou manutenção excepcional da conta ativa não representa novação, renúncia de direito, alteração contratual ou obrigação de conceder o mesmo benefício em cobranças futuras.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.7. Encargos por Atraso</h3>
            <p className="mb-4">
              O atraso ou inadimplemento de valores devidos poderá sujeitar o usuário à cobrança de multa moratória de até 2% sobre o valor em atraso, juros de mora de até 1% ao mês, calculados pro rata die, correção monetária pelo índice legalmente aplicável ou outro índice previsto na cobrança, além de tributos, tarifas bancárias, custos de cobrança e demais encargos permitidos pela legislação aplicável.
            </p>
            <p className="mb-4">A cobrança de encargos não impede a suspensão, restrição ou cancelamento da assinatura.</p>

            <h3 className="text-xl font-semibold mb-3">9.8. Reativação da Conta</h3>
            <p className="mb-4">
              A conta suspensa poderá ser reativada após a identificação e confirmação do pagamento dos valores pendentes, observados os prazos de compensação bancária, validação antifraude, atualização do status pelo provedor de pagamento e regras internas da Plataforma.
            </p>
            <p className="mb-4">
              A reativação poderá não ser imediata em casos de boleto, pagamento em análise, inconsistência cadastral, chargeback, contestação, cancelamento da assinatura ou encerramento definitivo da conta.
            </p>
            <p className="mb-4">
              A Plataforma poderá exigir a quitação integral dos valores pendentes, atualização dos dados de pagamento, contratação de novo plano ou nova assinatura para reativação do acesso.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.9. Cancelamento pelo Usuário — Planos Mensais</h3>
            <p className="mb-4">
              Nos <strong>planos com cobrança mensal sem compromisso anual</strong>, o usuário poderá cancelar sua assinatura a qualquer momento por meio das configurações da conta, quando disponível, ou pelos canais oficiais de atendimento.
            </p>
            <p className="mb-4">
              Salvo disposição em contrário, o cancelamento solicitado pelo usuário produzirá efeitos ao final do período de cobrança já pago, permanecendo o acesso disponível até o término do ciclo vigente.
            </p>
            <p className="mb-4">
              O cancelamento não gera, por si só, direito a reembolso proporcional, crédito ou estorno de valores referentes ao período já iniciado, já utilizado ou disponibilizado, salvo quando exigido por lei, por falha comprovada da Plataforma ou por política comercial expressamente aplicável.
            </p>
            <p className="mb-4">
              <strong>Importante:</strong> as regras de cancelamento dos <strong>Planos Anuais Parcelados</strong> (compromisso de 12 meses com cobrança mensal) estão descritas na Seção 9.12 e prevalecem sobre esta subseção quando houver conflito.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.10. Cancelamento pela Plataforma</h3>
            <p className="mb-4">
              A Plataforma poderá cancelar a assinatura, encerrar a conta ou recusar o uso atual ou futuro dos serviços em caso de inadimplência, chargeback, fraude, suspeita de fraude, violação destes Termos, uso indevido, risco jurídico, risco de segurança, violação de regras de terceiros integrados ou descumprimento de obrigações legais ou contratuais.
            </p>
            <p className="mb-4">
              Em caso de cancelamento por inadimplência, permanecerão devidos os valores vencidos, encargos aplicáveis e obrigações constituídas antes do cancelamento.
            </p>
            <p className="mb-4">
              A Plataforma poderá, quando comercialmente razoável, enviar aviso prévio e conceder oportunidade de correção antes do cancelamento definitivo, sem que isso seja condição obrigatória para suspensão imediata do acesso em caso de inadimplência.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.11. Reembolsos</h3>
            <p className="mb-4">
              Reembolsos serão avaliados caso a caso, conforme a legislação aplicável, a política comercial vigente e as circunstâncias específicas da solicitação.
            </p>
            <p className="mb-4">
              Em regra, não serão concedidos reembolsos por períodos já utilizados, períodos em que a conta permaneceu ativa, ausência de uso pelo usuário, esquecimento de cancelamento, falha de pagamento causada por dados incorretos, perda de acesso decorrente de inadimplência, downgrade, bloqueio por violação dos Termos ou cancelamento motivado por conduta do usuário.
            </p>
            <p className="mb-4">Nada nesta cláusula limita direitos obrigatórios do consumidor previstos na legislação aplicável.</p>

            <h3 className="text-xl font-semibold mb-3">9.12. Planos Anuais Parcelados (Compromisso de 12 Meses)</h3>
            <p className="mb-4">
              A FabChat oferece <strong>Planos Anuais Parcelados</strong>, nos quais o Cliente contrata o uso da plataforma por <strong>período mínimo de 12 (doze) meses</strong>, com pagamento dividido em <strong>12 (doze) parcelas mensais</strong> cobradas automaticamente no cartão ou outro meio de pagamento cadastrado.
            </p>
            <p className="mb-4">
              <strong>Natureza do contrato:</strong> trata-se de contrato de prestação continuada de serviços com vigência mínima anual. A cobrança mensal é <strong>forma de pagamento parcelado</strong> do valor total do período contratado, e <strong>não</strong> configura assinatura mensal livremente cancelável mês a mês. Ao escolher um Plano Anual Parcelado no checkout ou na página de planos, o Cliente declara ciência e concordância com o compromisso de 12 meses e com o valor total correspondente.
            </p>
            <p className="mb-4">
              Os planos anuais parcelados atualmente disponíveis, com valores indicativos vigentes na data desta versão dos Termos, são:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong>Essencial Anual Parcelado:</strong> 12 parcelas de R$ {STRIPE_PLANS.inicial.yearly.pricePerMonth.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/mês, totalizando R$ {STRIPE_PLANS.inicial.yearly.totalPrice.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} no período de 12 meses.
              </li>
              <li>
                <strong>Plus Anual Parcelado:</strong> 12 parcelas de R$ {STRIPE_PLANS.intermediario.yearly.pricePerMonth.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/mês, totalizando R$ {STRIPE_PLANS.intermediario.yearly.totalPrice.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} no período de 12 meses.
              </li>
              <li>
                <strong>PRO Anual Parcelado:</strong> 12 parcelas de R$ {STRIPE_PLANS.pro.yearly.pricePerMonth.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/mês, totalizando R$ {STRIPE_PLANS.pro.yearly.totalPrice.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} no período de 12 meses.
              </li>
            </ul>
            <p className="mb-4">
              Valores, descontos promocionais e condições exibidos no momento da contratação prevalecem sobre os valores indicativos acima, desde que confirmados no checkout ou na confirmação de pagamento.
            </p>
            <p className="mb-4">
              <strong>Obrigação de pagamento durante o período contratado:</strong> o Cliente permanece obrigado ao pagamento de todas as parcelas mensais até o término do período de 12 meses, ainda que:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>solicite cancelamento antecipado da assinatura;</li>
              <li>deixe de utilizar a plataforma por qualquer motivo;</li>
              <li>remova integrações, agentes ou usuários da conta;</li>
              <li>ocorra suspensão temporária por inadimplência posteriormente regularizada, sem prejuízo das parcelas vencidas.</li>
            </ul>
            <p className="mb-4">
              O cancelamento antecipado pelo Cliente <strong>não extingue</strong> as parcelas vincendas nem o saldo remanescente do compromisso anual, salvo disposição legal obrigatória em contrário ou acordo expresso por escrito com a FabChat.
            </p>
            <p className="mb-4">
              A FabChat poderá, a seu critério e sem obrigação de repetição em casos futuros, oferecer condições específicas para rescisão antecipada (por exemplo, quitação com desconto do saldo remanescente). Na ausência de acordo, permanecem exigíveis as parcelas restantes ou o valor total ainda devido do período contratado.
            </p>
            <p className="mb-4">
              <strong>Falha ou recusa de pagamento:</strong> a não quitação de qualquer parcela mensal constitui inadimplência e sujeita o Cliente às medidas das Seções 9.4 a 9.8, inclusive cobrança das parcelas em atraso, encargos permitidos por lei, suspensão do acesso e adoção de medidas de cobrança. A inadimplência não cancela automaticamente o compromisso anual nem reduz o valor total contratado.
            </p>
            <p className="mb-4">
              <strong>Renovação após os 12 meses:</strong> salvo cancelamento nos termos destes Termos antes do novo ciclo, ao término dos 12 meses o plano poderá ser renovado automaticamente nas mesmas condições do plano vigente ou nas condições então publicadas, conforme Seção 9.3, com comunicação prévia quando exigida por lei.
            </p>
            <p className="mb-4">
              <strong>Downgrade e upgrade:</strong> alterações de plano durante o período de compromisso anual poderão estar sujeitas a regras específicas de proporcionalidade, diferença de preço e permanência mínima, informadas no momento da solicitação ou no painel da conta.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.13. Direito de Arrependimento (CDC — Art. 49)</h3>
            <p className="mb-4">
              Quando a contratação ocorrer fora do estabelecimento comercial da FabChat, inclusive por meio da internet, aplicam-se as disposições do Código de Defesa do Consumidor sobre contratação à distância.
            </p>
            <p className="mb-4">
              O consumidor pessoa física, e demais hipóteses em que o CDC for aplicável, poderá exercer o <strong>direito de arrependimento em até {TERMS_WITHDRAWAL_DAYS} (sete) dias corridos</strong>, contados da contratação ou do ato de recebimento/confirmação do serviço, o que ocorrer por último, <strong>sem necessidade de justificativa e sem penalidade</strong>, nos termos do art. 49 do CDC.
            </p>
            <p className="mb-4">
              Para exercer o arrependimento, o Cliente deve enviar solicitação clara e inequívoca para <strong>atendimento.fabtech@gmail.com</strong>, informando o e-mail da conta, identificação da assinatura e data da contratação. A FabChat confirmará o recebimento e processará o pedido em prazo razoável.
            </p>
            <p className="mb-4">
              <strong>Início antecipado do serviço:</strong> se o Cliente solicitar ou consentir expressamente que a FabChat inicie a prestação do serviço digital durante o prazo de {TERMS_WITHDRAWAL_DAYS} dias, reconhece que, conforme permitido pelo art. 49, § único, do CDC, o reembolso poderá ser calculado de forma <strong>proporcional</strong> ao período em que o serviço foi efetivamente disponibilizado ou utilizado, deduzidos eventuais custos já incorridos comprovadamente relacionados à prestação.
            </p>
            <p className="mb-4">
              O exercício válido do direito de arrependimento dentro do prazo legal encerra o contrato e afasta a cobrança de parcelas futuras ainda não vencidas. Parcelas já processadas serão tratadas conforme estorno ou reembolso pelos meios de pagamento utilizados, observados prazos bancários e operacionais do provedor de pagamento.
            </p>
            <p className="mb-4">
              <strong>Após o prazo de arrependimento:</strong> vigoram as regras de permanência mínima dos Planos Anuais Parcelados (Seção 9.12) e as demais condições de cancelamento e inadimplência destes Termos. O direito de arrependimento não se confunde com cancelamento ordinário após o prazo legal.
            </p>
            <p className="mb-4">
              Nada nesta seção reduz direitos irrenunciáveis do consumidor previstos em lei. Em caso de conflito entre disposição contratual e norma cogente de proteção ao consumidor, prevalecerá a norma legal aplicável.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.14. Dados, Exportação, Retenção e Exclusão após Suspensão ou Cancelamento</h3>
            <p className="mb-4">
              A suspensão ou cancelamento da assinatura poderá limitar o acesso às funcionalidades da Plataforma, incluindo o acesso operacional a dados, relatórios, automações, integrações, históricos, arquivos ou conteúdos vinculados à conta.
            </p>
            <p className="mb-4">
              Quando aplicável, a Plataforma poderá manter acesso restrito à área da conta para regularização de pagamento, emissão de segunda via, atualização cadastral, suporte, cancelamento, consulta de informações essenciais ou solicitação de dados pessoais, conforme a legislação aplicável.
            </p>
            <p className="mb-4">
              O usuário é responsável por manter cópias, backups ou exportações dos dados, conteúdos, relatórios, arquivos e informações que inserir, gerar ou armazenar na Plataforma, salvo quando houver contratação expressa de serviço de backup ou obrigação legal específica em sentido diverso.
            </p>
            <p className="mb-4">
              Após o cancelamento da assinatura, encerramento da conta ou inadimplência prolongada, a Plataforma poderá excluir, anonimizar, bloquear ou tornar indisponíveis dados, arquivos, históricos, automações, integrações e conteúdos vinculados à conta, observando a Política de Privacidade, a legislação aplicável e os prazos técnicos, legais e operacionais da Plataforma.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.15. Preços e Cobrança dos Serviços Profissionais</h3>
            <p className="mb-4">
              Os valores referentes aos Serviços Profissionais são independentes dos valores da assinatura da Plataforma, salvo quando expressamente indicado de forma diversa na oferta ou proposta comercial.
            </p>
            <p className="mb-4">
              Os Serviços Profissionais poderão ser cobrados de forma única, parcelada, recorrente, por projeto, por etapa, por hora ou conforme outro critério informado no momento da contratação.
            </p>
            <p className="mb-4">
              Quando um Serviço Profissional for contratado por preço total fixo, eventual parcelamento constituirá apenas forma de pagamento do valor integral contratado e não transformará o projeto em serviço mensal ou contratação independente por parcela, salvo disposição expressa em contrário.
            </p>
            <p className="mb-4">
              A contratação de Serviços Profissionais não implica alteração automática no preço da assinatura da Plataforma.
            </p>
            <p className="mb-4">
              O cancelamento, suspensão, alteração ou ausência de utilização da assinatura não implica automaticamente cancelamento, redução, reembolso ou estorno dos Serviços Profissionais contratados, observadas as condições específicas da contratação e os direitos obrigatórios previstos na legislação aplicável.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.16. Inadimplência dos Serviços Profissionais</h3>
            <p className="mb-4">
              O não pagamento de valor devido em relação aos Serviços Profissionais poderá acarretar a suspensão da execução do projeto, de reuniões, do suporte relacionado à implementação e de outras atividades vinculadas ao respectivo serviço até a regularização dos valores pendentes.
            </p>
            <p className="mb-4">
              A suspensão por inadimplência não caracterizará atraso imputável à FabChat e poderá resultar na reprogramação do cronograma conforme a disponibilidade da equipe após a regularização.
            </p>
            <p className="mb-4">
              A suspensão da execução não extingue valores vencidos ou demais obrigações de pagamento já constituídas.
            </p>
            <p className="mb-4">
              Aplicam-se aos valores em atraso, quando cabíveis, as regras gerais de inadimplência, cobrança e encargos previstas nestes Termos.
            </p>

            <h3 className="text-xl font-semibold mb-3">9.17. Cancelamento dos Serviços Profissionais</h3>
            <p className="mb-4">
              As condições de cancelamento poderão variar conforme a natureza do Serviço Profissional, especialmente quando houver projeto de preço fechado, reserva de agenda, alocação antecipada de equipe, horas técnicas, integrações ou atividades especificamente destinadas ao Cliente.
            </p>
            <p className="mb-4">
              Após o início da execução, o cancelamento solicitado pelo Cliente não gera automaticamente direito à devolução dos valores já pagos nem extingue automaticamente valores relacionados a atividades já executadas, etapas iniciadas, recursos alocados ou obrigações financeiras constituídas.
            </p>
            <p className="mb-4">
              Quando a proposta comercial ou instrumento de contratação estabelecer expressamente que o Serviço Profissional possui preço fechado e compromisso de pagamento integral, eventual cancelamento após o início não afastará o pagamento do saldo contratado, inclusive das parcelas vincendas, observados os direitos obrigatórios previstos na legislação aplicável.
            </p>
            <p className="mb-4">
              Para fins desta cláusula, poderão caracterizar início da execução, conforme o caso, atividades como planejamento, reunião inicial, análise de materiais, reserva efetiva de equipe, preparação do ambiente, organização ou importação de dados, configuração, parametrização, integração, testes ou outras atividades previstas no escopo.
            </p>
            <p className="mb-4">
              Caso o cancelamento seja solicitado antes do início efetivo da execução, eventual reembolso observará as condições específicas da contratação, os custos já incorridos e a legislação aplicável.
            </p>
            <p className="mb-4">
              Nada nesta seção limita direitos irrenunciáveis assegurados ao consumidor ou outros direitos obrigatórios previstos na legislação aplicável.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Disponibilidade e Uptime</h2>
            <p className="mb-4">
              Nos esforçamos para manter nossos serviços disponíveis 24/7, mas não garantimos disponibilidade ininterrupta. Podemos realizar manutenções programadas ou de emergência que podem resultar em interrupções temporárias.
            </p>
            <p className="mb-4">
              Não nos responsabilizamos por perdas ou danos resultantes de indisponibilidade temporária dos serviços, exceto quando resultante de negligência grosseira ou dolo de nossa parte.
            </p>
            <h3 className="text-xl font-semibold mb-3">10.1. Força Maior e Caso Fortuito</h3>
            <p className="mb-4">
              A FabChat não se responsabiliza por inadimplemento ou atraso nas obrigações decorrentes de eventos de <strong>força maior</strong> ou <strong>caso fortuito</strong>, incluindo mas não limitado a: catástrofes naturais, guerra, atos terroristas, pandemia, bloqueio ou determinação governamental, falha grave de infraestrutura de terceiros essenciais (energia, telecomunicações, hospedagem), greves que afetem serviços essenciais, ou qualquer evento imprevisível e inevitável que esteja fora de nosso controle razoável.
            </p>
            <p className="mb-4">
              Em tais circunstâncias, as obrigações afetadas ficarão suspensas durante o período do evento, e não haverá direito a indenização ou reembolso por indisponibilidade ou prejuízos decorrentes desses fatos. A FabChat comunicará aos usuários, quando possível, sobre a ocorrência e a duração estimada do evento.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Limitação de Responsabilidade</h2>
            <p className="mb-4">
              <strong>NA MEDIDA MÁXIMA PERMITIDA POR LEI:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Nossos serviços são fornecidos "COMO ESTÃO" e "CONFORME DISPONÍVEIS", sem garantias de qualquer tipo, expressas ou implícitas</li>
              <li>Não garantimos que os serviços atenderão a todos os seus requisitos ou estarão sempre disponíveis, seguros ou livres de erros</li>
              <li>Não nos responsabilizamos por perdas de dados, lucros, receitas, oportunidades de negócio ou danos indiretos, incidentais ou consequenciais</li>
              <li>Nossa responsabilidade total não excederá o valor pago por você nos últimos 12 meses pelos serviços</li>
            </ul>
            <p className="mb-4">
              Esta limitação não se aplica a danos resultantes de negligência grosseira ou dolo de nossa parte, ou quando a lei não permitir tal limitação.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Indenização</h2>
            <p className="mb-4">
              Você concorda em indenizar, defender e isentar a FabChat, seus diretores, funcionários e agentes de quaisquer reivindicações, danos, obrigações, perdas, responsabilidades, custos ou dívidas, e despesas (incluindo honorários advocatícios) decorrentes de:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Seu uso ou mau uso dos serviços</li>
              <li>Violação de estes Termos de Serviço</li>
              <li>Violação de qualquer lei ou direito de terceiros</li>
              <li>Processamento inadequado ou não autorizado de dados pessoais de terceiros</li>
              <li>Qualquer conteúdo ou dados que você forneça ou processe através de nossos serviços</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Rescisão</h2>
            <p className="mb-4">
              Podemos suspender ou encerrar sua conta e acesso aos serviços imediatamente, sem aviso prévio, se você:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Violar estes Termos de Serviço</li>
              <li>Utilizar os serviços de forma ilegal ou não autorizada</li>
              <li>Não efetuar pagamentos devidos</li>
              <li>Realizar atividades que possam prejudicar nossos serviços ou outros usuários</li>
            </ul>
            <p className="mb-4">
              Após a rescisão, seu direito de acesso e uso dos serviços cessará imediatamente. Podemos reter seus dados por um período razoável após a rescisão, conforme necessário para cumprir obrigações legais ou proteger nossos direitos.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">14. Modificações dos Termos</h2>
            <p className="mb-4">
              Reservamo-nos o direito de modificar estes Termos de Serviço a qualquer momento. Notificaremos você sobre alterações significativas publicando os termos atualizados nesta página e atualizando a data de "Última atualização".
            </p>
            <p className="mb-4">
              Seu uso continuado dos serviços após as modificações constitui sua aceitação dos termos modificados. Se você não concordar com as modificações, deve cessar o uso dos serviços e cancelar sua conta.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">15. Contrato de Processamento de Dados (DPA)</h2>
            
            <h3 className="text-xl font-semibold mb-3">15.1. Natureza do Processamento</h3>
            <p className="mb-4">
              Ao utilizar nossos serviços, você (o "Controlador") nos autoriza (o "Operador") a processar dados pessoais de seus clientes e leads em seu nome. Este processamento é regido pela Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018) e por este Contrato de Processamento de Dados.
            </p>

            <h3 className="text-xl font-semibold mb-3">15.2. Tipos de Dados Processados</h3>
            <p className="mb-4">Os dados pessoais processados incluem, mas não se limitam a:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Dados de Identificação:</strong> Nome, telefone, e-mail, username de redes sociais</li>
              <li><strong>Dados de Comunicação:</strong> Histórico de conversas, mensagens, áudios transcritos</li>
              <li><strong>Dados de Comportamento:</strong> Interações com o agente de IA, estágio no funil de vendas</li>
              <li><strong>Dados de Transação:</strong> Produtos de interesse, agendamentos, preferências</li>
              <li><strong>Metadados:</strong> Timestamps, canal de comunicação, informações de perfil de redes sociais</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">15.3. Finalidade do Processamento</h3>
            <p className="mb-4">Os dados são processados exclusivamente para:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Fornecer atendimento automatizado via agentes de IA</li>
              <li>Gerenciar conversas e histórico de interações</li>
              <li>Qualificar leads e gerenciar funil de vendas</li>
              <li>Agendar compromissos e enviar lembretes</li>
              <li>Gerar relatórios e análises para o Controlador</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">15.4. Obrigações do Operador (FabChat)</h3>
            <p className="mb-4">Nós nos comprometemos a:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Processar dados pessoais apenas conforme suas instruções documentadas</li>
              <li>Garantir que pessoas autorizadas a processar dados estejam comprometidas com confidencialidade</li>
              <li>Implementar medidas técnicas e organizacionais apropriadas para proteger os dados</li>
              <li>Não contratar suboperadores sem sua autorização prévia (lista disponível abaixo)</li>
              <li>Auxiliá-lo no atendimento de solicitações de titulares de dados (LGPD Art. 18)</li>
              <li>Notificá-lo sobre incidentes de segurança em até 24 horas após a descoberta</li>
              <li>Deletar ou devolver todos os dados pessoais ao término do contrato</li>
              <li>Disponibilizar informações necessárias para demonstrar conformidade com a LGPD</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">15.5. Obrigações do Controlador (Você)</h3>
            <p className="mb-4">Você se compromete a:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Garantir que possui base legal adequada para coletar e processar os dados pessoais</li>
              <li>Obter consentimento dos titulares quando necessário</li>
              <li>Informar os titulares sobre o processamento de seus dados, incluindo o uso de nossos serviços</li>
              <li>Garantir que as instruções para processamento estejam em conformidade com a LGPD</li>
              <li>Responder prontamente a solicitações de titulares de dados</li>
              <li>Não nos instruir a processar dados de forma ilegal ou em violação à LGPD</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">15.6. Suboperadores</h3>
            <p className="mb-4">
              Utilizamos os seguintes suboperadores para fornecer nossos serviços. Ao aceitar estes termos, você autoriza o uso destes suboperadores:
            </p>
            <div className="bg-muted p-4 rounded-lg mb-4">
              <ul className="space-y-2 text-sm">
                <li><strong>Supabase Inc.</strong> - Hospedagem de banco de dados e autenticação (EUA)</li>
                <li><strong>OpenAI LLC</strong> - Processamento de inteligência artificial (EUA)</li>
                <li><strong>Stripe Inc.</strong> - Processamento de pagamentos (EUA)</li>
                <li><strong>Meta Platforms Inc.</strong> - API do Instagram (EUA)</li>
                <li><strong>Google LLC</strong> - API do Google Calendar (EUA)</li>
              </ul>
            </div>
            <p className="mb-4">
              Notificaremos você com 30 dias de antecedência sobre qualquer mudança nos suboperadores, permitindo que você objete ao uso de um novo suboperador.
            </p>

            <h3 className="text-xl font-semibold mb-3">15.7. Transferência Internacional de Dados</h3>
            <p className="mb-4">
              Os dados pessoais podem ser transferidos e processados em países fora do Brasil, incluindo Estados Unidos. Garantimos que tais transferências são realizadas em conformidade com a LGPD, utilizando:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Cláusulas contratuais padrão aprovadas pela ANPD</li>
              <li>Garantias de que os suboperadores implementam medidas de segurança adequadas</li>
              <li>Compromisso de cooperar com autoridades de proteção de dados brasileiras</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">15.8. Medidas de Segurança</h3>
            <p className="mb-4">Implementamos as seguintes medidas técnicas e organizacionais:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Criptografia de dados em trânsito (TLS/SSL) e em repouso</li>
              <li>Controles de acesso baseados em função (RBAC)</li>
              <li>Autenticação multifator para acesso administrativo</li>
              <li>Logs de auditoria de todas as operações sensíveis</li>
              <li>Backups regulares e plano de recuperação de desastres</li>
              <li>Monitoramento contínuo de segurança e detecção de anomalias</li>
              <li>Políticas de retenção e exclusão de dados</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">15.9. Incidentes de Segurança</h3>
            <p className="mb-4">
              Em caso de violação de dados pessoais, notificaremos você em até 24 horas após tomarmos conhecimento do incidente, fornecendo:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Descrição da natureza da violação</li>
              <li>Categorias e número aproximado de titulares afetados</li>
              <li>Categorias e número aproximado de registros de dados afetados</li>
              <li>Medidas tomadas ou propostas para remediar a violação</li>
              <li>Medidas para mitigar possíveis efeitos adversos</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">15.10. Direitos dos Titulares</h3>
            <p className="mb-4">
              Auxiliaremos você no atendimento de solicitações de titulares de dados conforme LGPD Art. 18, incluindo:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Confirmação de existência de tratamento</li>
              <li>Acesso aos dados</li>
              <li>Correção de dados incompletos, inexatos ou desatualizados</li>
              <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos</li>
              <li>Portabilidade dos dados</li>
              <li>Eliminação dos dados tratados com consentimento</li>
              <li>Informação sobre compartilhamento de dados</li>
              <li>Revogação do consentimento</li>
            </ul>
            <p className="mb-4">
              Você é responsável por responder diretamente aos titulares. Forneceremos assistência técnica razoável dentro de 5 dias úteis após sua solicitação.
            </p>

            <h3 className="text-xl font-semibold mb-3">15.11. Auditoria e Fiscalização</h3>
            <p className="mb-4">
              Mediante solicitação razoável e com aviso prévio de 30 dias, você pode:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Solicitar relatórios de conformidade e certificações de segurança</li>
              <li>Realizar auditorias ou inspeções (limitadas a uma vez por ano)</li>
              <li>Solicitar informações sobre nossas práticas de processamento de dados</li>
            </ul>
            <p className="mb-4">
              Auditorias presenciais devem ser coordenadas com antecedência e não podem interferir com nossas operações comerciais normais.
            </p>

            <h3 className="text-xl font-semibold mb-3">15.12. Duração e Término</h3>
            <p className="mb-4">
              Este DPA permanece em vigor enquanto processarmos dados pessoais em seu nome. Ao término:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Deletaremos todos os dados pessoais dentro de 30 dias, exceto quando a lei exigir retenção</li>
              <li>Você pode solicitar a devolução dos dados em formato estruturado antes da exclusão</li>
              <li>Forneceremos certificação por escrito da exclusão dos dados, se solicitado</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">15.13. Responsabilidade</h3>
            <p className="mb-4">
              Cada parte é responsável perante a outra por danos causados por violação deste DPA, conforme permitido pela LGPD e legislação aplicável. Nossa responsabilidade total está limitada conforme estabelecido na Seção 11 destes Termos de Serviço.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">16. Lei Aplicável e Jurisdição</h2>
            <p className="mb-4">
              Estes Termos de Serviço são regidos pelas leis da República Federativa do Brasil. Qualquer disputa relacionada a estes termos ou aos nossos serviços será resolvida nos tribunais competentes do Brasil.
            </p>
            <p className="mb-4">
              Antes de iniciar qualquer ação judicial, as partes concordam em tentar resolver disputas através de negociação de boa fé. Se a negociação não for bem-sucedida, as partes podem buscar mediação ou arbitragem conforme acordado.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">17. Disposições Gerais</h2>
            
            <h3 className="text-xl font-semibold mb-3">17.1. Acordo Completo</h3>
            <p className="mb-4">
              Estes Termos de Serviço, juntamente com nossa Política de Privacidade e Política de Cookies, constituem o acordo completo entre você e a FabChat em relação ao uso dos serviços.
            </p>

            <h3 className="text-xl font-semibold mb-3">17.2. Divisibilidade</h3>
            <p className="mb-4">
              Se qualquer disposição destes termos for considerada inválida ou inexequível, as demais disposições permanecerão em pleno vigor e efeito.
            </p>

            <h3 className="text-xl font-semibold mb-3">17.3. Renúncia</h3>
            <p className="mb-4">
              O fato de não exercermos ou aplicarmos qualquer direito ou disposição destes termos não constitui uma renúncia a tal direito ou disposição.
            </p>

            <h3 className="text-xl font-semibold mb-3">17.4. Cessão</h3>
            <p className="mb-4">
              Você não pode ceder ou transferir estes termos ou seus direitos sob estes termos sem nossa autorização prévia por escrito. Podemos ceder estes termos ou nossos direitos a qualquer momento sem aviso prévio.
            </p>

            <h3 className="text-xl font-semibold mb-3">17.5. Notificações</h3>
            <p className="mb-4">
              As notificações da FabChat a você serão realizadas por e-mail ao endereço cadastrado em sua conta e/ou por aviso na plataforma (mensagem na área logada). As notificações se consideram recebidas em até 48 (quarenta e oito) horas após o envio do e-mail ou a publicação do aviso na plataforma. É sua responsabilidade manter o e-mail cadastrado atualizado e verificar periodicamente a plataforma.
            </p>

            <h3 className="text-xl font-semibold mb-3">17.6. Idioma e Versão Prevalecente</h3>
            <p className="mb-4">
              Estes Termos de Serviço são redigidos em português do Brasil. Em caso de existir versão traduzida para outro idioma, a versão em <strong>português</strong> prevalecerá em caso de conflito ou divergência de interpretação.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">18. Contato</h2>
            <p className="mb-4">
              Se você tiver dúvidas sobre estes Termos de Serviço, entre em contato conosco:
            </p>
            <div className="bg-muted p-4 rounded-lg mb-4">
              <p className="mb-2"><strong>FabChat</strong></p>
              <p className="mb-2">E-mail: <strong>atendimento.fabtech@gmail.com</strong></p>
              <p className="mb-2">Suporte: <strong>atendimento.fabtech@gmail.com</strong></p>
            </div>
          </section>

          <div className="mt-12 pt-8 border-t border-border">
            <Link 
              to="/" 
              className="text-primary hover:underline"
            >
              ← Voltar ao início
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

